import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { rebindCartRecommendedScroll } from '@theme/ug-cart-recommended';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/** 数量输入的防抖时长（按行独立计时） */
const QUANTITY_DEBOUNCE_MS = 300;

/** 更新失败时的兜底提示；section 上配了 data-update-error-text 就用可翻译的那份 */
const DEFAULT_UPDATE_ERROR_TEXT = 'Something went wrong. Please refresh the page and try again.';

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  /**
   * 按行独立的防抖器。
   * 组件级单 timer 会让 300ms 内改两行时只发出最后一行，行内容错乱。
   * @type {Map<string, ((event: QuantitySelectorUpdateEvent) => void) & { cancel(): void }>}
   */
  #debouncedByRow = new Map();
  /** 串行改购物车，避免连点数量/删除时行号错位 */
  #updateQueue = Promise.resolve();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);

    for (const debounced of this.#debouncedByRow.values()) debounced.cancel();
    this.#debouncedByRow.clear();
  }

  /**
   * quantity-selector:update 的入口。
   * 归属过滤必须放在防抖之前：这是冒泡到 document 的全局事件，
   * 别处（快速加购弹窗、抽屉）的数量事件进了防抖会把本组件待发的改动挤掉。
   * @param {Event} event - The event.
   */
  #handleQuantitySelectorUpdate = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) return;

    const quantityEvent = /** @type {QuantitySelectorUpdateEvent} */ (event);
    const { cartLine, cartLineKey } = quantityEvent.detail;
    const rowId = (typeof cartLineKey === 'string' && cartLineKey) || `line:${cartLine}`;

    let debounced = this.#debouncedByRow.get(rowId);
    if (!debounced) {
      debounced = debounce(
        /** @param {QuantitySelectorUpdateEvent} rowEvent */
        (rowEvent) => this.#onQuantityChange(rowEvent),
        QUANTITY_DEBOUNCE_MS
      );
      this.#debouncedByRow.set(rowId, debounced);
    }

    debounced(quantityEvent);
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line, cartLineKey } = event.detail;
    const key = typeof cartLineKey === 'string' ? cartLineKey : '';

    if (!line && !key) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line, event);
    }

    this.updateQuantity({
      line,
      key,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#findCartRow(key, line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {Event} [event] - 点击事件，用来读按钮上的 key
   */
  onLineItemRemove(line, event) {
    const clickTarget = event?.target instanceof Element ? event.target : null;
    const resolvedKey =
      clickTarget?.closest?.('[data-line-key]')?.getAttribute('data-line-key') ||
      (typeof event?.detail?.cartLineKey === 'string' ? event.detail.cartLineKey : '') ||
      '';

    this.updateQuantity({
      line,
      key: resolvedKey,
      quantity: 0,
      action: 'clear',
    });

    // 抽屉里先删行会让吸底结算区上跳，等 morph 一次换完
    if (this.closest('cart-drawer-component')) return;

    const cartItemRowToRemove = this.#findCartRow(resolvedKey, line);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.key] - 行项目 key，优先于 line
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    this.#updateQueue = this.#updateQueue.then(() => this.#updateQuantityNow(config)).catch((error) => {
      console.error(error);
    });
  }

  /**
   * @param {Object} config
   * @param {number} config.line
   * @param {string} [config.key]
   * @param {number} config.quantity
   * @param {string} config.action
   */
  #updateQuantityNow(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, key, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const identity = key ? { id: key } : { line };
    const body = JSON.stringify({
      ...identity,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#showRowError({ line, key }, parsedResponseText.errors);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        // morphSection 是 async 且会 throw，同步调用会变成 unhandled rejection；
        // 单独兜住它，避免 morph 失败被外层 catch 误判成「更新失败」而回滚输入框
        try {
          await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        } catch (morphError) {
          console.error('[cart-items] 购物车 section 更新失败', morphError);
        }
        rebindCartRecommendedScroll();
      })
      .catch((error) => {
        // 网络失败 / 503 / Shopify 返回 HTML 错误页（JSON 解析失败）都落在这里。
        // 乐观更新已经把新数量写进输入框了，不回滚 + 不提示的话，
        // 用户看到的是新数量、服务端却没变，直到结算才发现对不上。
        console.error('[cart-items] 更新数量失败', error);
        resetShimmer(this);
        this.#showRowError({ line, key }, this.dataset.updateErrorText || DEFAULT_UPDATE_ERROR_TEXT);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * 把该行的输入框回滚成服务端数量，并在行下方展示错误提示。
   * 服务端业务错误和请求整体失败共用这一条提示通道。
   * 按 key 定位行：只传 line 时它是 NaN，按行号索引又会在 morph 重排后指到别的行。
   * @param {{ line?: number, key?: string }} config - 行标识
   * @param {string} message - 展示给用户的错误文案
   */
  #showRowError = (config, message) => {
    const { line, key } = config;

    const quantityInput = this.#findCartRow(key, line)?.querySelector('input');
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
    }

    // 每行都渲染了 data-line-key，key 缺失说明调用方传参有问题，没有可靠的兜底定位方式
    if (!key) {
      console.warn('[cart-items] 行 key 缺失，无法定位错误提示容器：', message);
      return;
    }

    const errorScope = this.querySelector(`.cart-items__error-row[data-key="${CSS.escape(key)}"]`);
    // hidden 挂在内层 .cart-item__error 上，外层 td 去掉这个类不起作用
    const errorBox = errorScope?.querySelector('.cart-item__error');
    const errorText = errorScope?.querySelector('.cart-item__error-text');

    // 行可能已被上一次 morph 换掉，静默降级好过抛错——抛出去只会被 catch 吞掉，用户什么都看不到
    if (!(errorBox instanceof HTMLElement) || !(errorText instanceof HTMLElement)) {
      console.warn('[cart-items] 找不到错误提示容器：', message);
      return;
    }

    errorText.textContent = message;
    errorBox.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    const eventDetail = /** @type {any} */ (event.detail);

    if (event instanceof DiscountUpdateEvent) {
      this.#refreshSection(eventDetail.resource?.sections?.[this.sectionId]);
      return;
    }
    if (event.target === this) return;

    // 有码加购 pending：抽屉未开时跳过无折扣首刷；已打开则立即 morph，避免角标与列表不一致
    if (window.UGAutoDiscountBridge?.holdDrawer && !this.#isCartDrawerOpen()) return;

    this.#refreshSection(event.detail.data.sections?.[this.sectionId]);
  };

  /**
   * 用现成的 section HTML morph；没有就重新拉一次。
   * morphSection / renderSection 都是 async 且会 throw，必须自己兜住，
   * 否则变成 unhandled rejection，用户侧表现为「什么都没发生」。
   * @param {string | undefined} cartItemsHtml
   */
  #refreshSection(cartItemsHtml) {
    const done = cartItemsHtml
      ? morphSection(this.sectionId, cartItemsHtml)
      : sectionRenderer.renderSection(this.sectionId, { cache: false });

    done
      .then(() => rebindCartRecommendedScroll())
      .catch((error) => console.error('[cart-items] 购物车 section 刷新失败', error));
  }

  /** 抽屉已打开时列表可见，hold 期间不能跳过 morph */
  #isCartDrawerOpen() {
    const dialog = document.querySelector('cart-drawer-component dialog');
    return dialog instanceof HTMLDialogElement && dialog.open;
  }

  /**
   * @param {string} [key]
   * @param {number} [line]
   */
  #findCartRow(key, line) {
    // ref 名以 [] 结尾时 component.js 保证收集结果要么是数组、要么是 undefined
    const list = this.refs.cartItemRows ?? [];
    if (key) {
      return list.find((row) => row.dataset.key === key);
    }
    return list[Number(line) - 1];
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
